import { useId, useMemo, useState } from 'react'
import { LuChevronDown } from 'react-icons/lu'
import { useSubscriptions } from '@cannect/services/resources/recurrency/hooks'
import type { SubscriptionView } from '@cannect/services/resources/recurrency/types'
import { cn } from '@cannect/lib/utils'
import { subscriptionsCopy } from '../copy'
import { useListDeepLink } from '../hooks/useSubscriptionDeepLink'
import { LoadError } from './LoadError'
import { SubscriptionCard } from './SubscriptionCard'
import { SubscriptionListSkeleton } from './SubscriptionListSkeleton'
import { SubscriptionsEmpty } from './SubscriptionsEmpty'

const isOpen = (sub: SubscriptionView) => sub.status === 'active' || sub.status === 'paused'

export const SubscriptionList = () => {
  const { data, isLoading, isError, refetch } = useSubscriptions()
  const { highlight } = useListDeepLink(data)
  const [historyOpen, setHistoryOpen] = useState(false)
  const historyId = useId()

  const { current, past } = useMemo(() => {
    const list = data || []
    return {
      // Titular primeiro, depois dependentes (ordem estável do serviço).
      current: list.filter(isOpen).sort((a, b) => Number(a.isDependent) - Number(b.isDependent)),
      past: list.filter((sub) => !isOpen(sub))
    }
  }, [data])

  if (isLoading) return <SubscriptionListSkeleton />

  return (
    <section className="flex w-full flex-col gap-4" aria-labelledby="subscriptions-title">
      <h1 id="subscriptions-title" className="text-2xl font-semibold text-neutral-800">
        {subscriptionsCopy.title}
      </h1>

      {isError && <LoadError onRetry={() => refetch()} />}

      {!isError && !current.length && !past.length && <SubscriptionsEmpty />}

      {current.map((sub) => (
        <SubscriptionCard key={sub.id} subscription={sub} highlight={highlight} />
      ))}

      {past.length > 0 && (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            aria-expanded={historyOpen}
            aria-controls={historyId}
            onClick={() => setHistoryOpen((value) => !value)}
            className="flex items-center gap-2 self-start border-none bg-transparent p-0 text-base font-semibold text-neutral-700">
            {subscriptionsCopy.history(past.length)}
            <LuChevronDown className={cn('h-4 w-4 transition-transform', historyOpen && 'rotate-180')} aria-hidden />
          </button>
          {historyOpen && (
            <div id={historyId} className="flex flex-col gap-3">
              {past.map((sub) => (
                <SubscriptionCard key={sub.id} subscription={sub} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
